/**
 * Phase 5 — Architect Assigned Requests page
 * Route: /architect/requests
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ClipboardList, CheckCircle2, XCircle, PlayCircle, Flag, RefreshCw, Sparkles } from 'lucide-react';
import theme from '../theme';
import AssistantDraftPanel from '../components/AssistantDraftPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLOR = {
  assigned: theme.colors.status.info, accepted: theme.colors.status.success,
  declined: theme.colors.status.error, in_progress: theme.colors.status.warning,
  completed: theme.colors.status.success, canceled: theme.colors.text.muted,
  rejected: theme.colors.status.error, archived: theme.colors.text.muted,
  submitted: theme.colors.status.info, admin_review: theme.colors.status.warning,
  draft: theme.colors.text.muted,
};

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, padding: 20, boxShadow: theme.colors.shadows.sm,
};
const inputStyle = {
  width: '100%', padding: '9px 11px', border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.sm, fontSize: 13, outline: 'none', boxSizing: 'border-box',
};

function StatusPill({ status }) {
  const c = STATUS_COLOR[status] || theme.colors.text.muted;
  return (
    <span data-testid={`arch-req-status-${status}`} style={{ padding: '3px 10px',
      borderRadius: 999, background: `${c}1A`, color: c, fontSize: 11,
      fontWeight: 800, textTransform: 'uppercase' }}>
      {(status || '').replace(/_/g, ' ')}
    </span>
  );
}

export default function ArchitectRequestsPage() {
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});
  const [declineReason, setDeclineReason] = useState({});
  const [expandDraft, setExpandDraft] = useState({});
  const [architectApproved, setArchitectApproved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/architect/profile`, auth);
        setArchitectApproved(res.data?.architect?.approval_status === 'approved');
      } catch (_) { /* keep default false */ }
    })();
  }, [auth]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/architect/requests`, auth);
      setRows(res.data?.requests || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load requests');
    } finally { setLoading(false); }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const decide = async (id, action) => {
    try {
      await axios.patch(`${API}/architect/requests/${id}/decision`,
        { action, decline_reason: declineReason[id] || null }, auth);
      toast.success(action === 'accept' ? 'Accepted' : 'Declined');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
  };
  const work = async (id, action) => {
    try {
      await axios.patch(`${API}/architect/requests/${id}/work`,
        { action, architect_notes: notes[id] || null }, auth);
      toast.success(action.replace(/_/g, ' '));
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
  };

  return (
    <div data-testid="architect-requests-page" style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 4 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ClipboardList size={22} color={theme.colors.primary} />
            <div style={{ fontSize: 22, fontWeight: 900 }}>Assigned Architect Requests</div>
          </div>
          <div style={{ color: theme.colors.text.muted, fontSize: 13, marginTop: 4, maxWidth: 720 }}>
            Review assigned Architect request packets, accept or decline requests, and update request progress.
          </div>
        </div>
        <button data-testid="arch-req-refresh-btn" onClick={load}
          style={{ padding: '9px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}`,
            background: theme.colors.bg.card, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div style={{ padding: 10, background: '#FFF7ED', border: '1px solid #FED7AA',
        borderRadius: theme.radius.sm, marginTop: 14, marginBottom: 14, fontSize: 12, color: '#7C2D12' }}>
        Kickback Contract does not guarantee approval, availability, financial outcomes, investment performance,
        payouts, or legal compliance. Regulated activities may require properly licensed professionals
        and separate agreements.
      </div>

      <div style={{ ...cardStyle }}>
        {loading ? (
          <div data-testid="arch-req-loading" style={{ padding: 18, color: theme.colors.text.muted }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div data-testid="arch-req-empty" style={{ padding: 24, textAlign: 'center', color: theme.colors.text.muted }}>
            No Architect requests assigned yet.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {rows.map(r => (
              <div key={r.id} data-testid={`arch-req-row-${r.id}`} style={{ padding: 12,
                background: theme.colors.bg.secondary, borderRadius: theme.radius.sm }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ fontWeight: 800 }}>{r.kickback_name}</div>
                  <StatusPill status={r.status} />
                  <span style={{ fontSize: 11, color: theme.colors.text.muted }}>
                    {r.kickback_type} · {r.requested_specialty} · risk: {r.risk_category}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: theme.colors.text.secondary, marginTop: 4 }}>
                  Business: <b>{r.business_name}</b> · Requestor: {r.creator_email}
                </div>
                {r.program_summary && (
                  <div style={{ fontSize: 12, color: theme.colors.text.secondary, marginTop: 4 }}>
                    {r.program_summary}
                  </div>
                )}
                {r.compliance_notes && (
                  <div style={{ marginTop: 6, padding: 8, background: '#FFFBEB',
                    border: '1px solid #FDE68A', borderRadius: theme.radius.sm, fontSize: 12, color: '#78350F' }}>
                    <b>Compliance notes:</b> {r.compliance_notes}
                  </div>
                )}

                {r.status === 'assigned' && (
                  <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input data-testid={`arch-decline-reason-${r.id}`} placeholder="Decline reason (optional)"
                      value={declineReason[r.id] || ''} style={{ ...inputStyle, maxWidth: 260 }}
                      onChange={e => setDeclineReason(d => ({ ...d, [r.id]: e.target.value }))} />
                    <button data-testid={`arch-accept-${r.id}`} onClick={() => decide(r.id, 'accept')}
                      style={{ padding: '8px 12px', borderRadius: theme.radius.sm, border: 'none',
                        background: theme.colors.status.success, color: '#fff', cursor: 'pointer', fontWeight: 700,
                        display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <CheckCircle2 size={14} /> Accept
                    </button>
                    <button data-testid={`arch-decline-${r.id}`} onClick={() => decide(r.id, 'decline')}
                      style={{ padding: '8px 12px', borderRadius: theme.radius.sm, border: 'none',
                        background: theme.colors.status.error, color: '#fff', cursor: 'pointer', fontWeight: 700,
                        display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <XCircle size={14} /> Decline
                    </button>
                  </div>
                )}

                {(r.status === 'accepted' || r.status === 'in_progress') && (
                  <div style={{ marginTop: 10 }}>
                    <textarea data-testid={`arch-work-notes-${r.id}`}
                      placeholder="Architect review notes (visible on completion)…"
                      style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit' }}
                      value={notes[r.id] !== undefined ? notes[r.id] : (r.architect_notes || '')}
                      onChange={e => setNotes(n => ({ ...n, [r.id]: e.target.value }))} />
                    <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {r.status === 'accepted' && (
                        <button data-testid={`arch-in-progress-${r.id}`} onClick={() => work(r.id, 'mark_in_progress')}
                          style={{ padding: '8px 12px', borderRadius: theme.radius.sm, border: 'none',
                            background: theme.colors.status.warning, color: '#fff', cursor: 'pointer', fontWeight: 700,
                            display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <PlayCircle size={14} /> Mark In Progress
                        </button>
                      )}
                      <button data-testid={`arch-complete-${r.id}`} onClick={() => work(r.id, 'mark_completed')}
                        style={{ padding: '8px 12px', borderRadius: theme.radius.sm, border: 'none',
                          background: theme.colors.status.success, color: '#fff', cursor: 'pointer', fontWeight: 700,
                          display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Flag size={14} /> Mark Completed
                      </button>
                    </div>
                  </div>
                )}

                {['assigned', 'accepted', 'in_progress'].includes(r.status) && (
                  <div style={{ marginTop: 10 }}>
                    <button data-testid={`arch-toggle-draft-${r.id}`}
                      onClick={() => setExpandDraft(s => ({ ...s, [r.id]: !s[r.id] }))}
                      style={{ padding: '8px 12px', borderRadius: theme.radius.sm,
                        border: `1px solid ${theme.colors.borders}`, background: theme.colors.bg.card,
                        cursor: 'pointer', fontWeight: 700,
                        display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <Sparkles size={14} /> {expandDraft[r.id] ? 'Hide Assistant Draft' : 'Assistant Draft'}
                    </button>
                    {expandDraft[r.id] && (
                      <AssistantDraftPanel
                        requestId={r.id}
                        role="architect"
                        status={r.status}
                        architectApproved={architectApproved}
                        onRefresh={load}
                      />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
